import { Box, Button, Collapse, Fade, Tooltip, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table';
import React from 'react';
import dialog from '~/components/Commons/ActionConfirmDialog/useDialog';
import Card from '~/components/Commons/Card/Card';
import CardBody from '~/components/Commons/Card/CardBody';
import DataTable from '~/components/Commons/DataTable/DataTable';
import GridContainer from '~/components/Commons/Grid/GridContainer';
import GridItem from '~/components/Commons/Grid/GridItem';
import { CustomNavLink } from '~/components/Commons/Link/Link';
import StyledCheckbox from '~/components/Form/Checkbox';
import Switch from '~/components/Form/Switch';
import TextField from '~/components/Form/TextField/TextField';
import useNotifications from '~/context/Notifications';
import { useClientSessionQuery } from '~/hooks/queries/useSessionPlQuery';
import { customFetch } from '~/utils/customFetch';
import { ForecastProvider, Forecaster } from '../../AccountParameters/Forecaster/Forecaster';
import styles from './styles';
import { CapReturnData, FormattedCapsData, useOverallCaps } from './utils';

const updateOverallCaps = async (overallData: FormattedCapsData) => {
  const APIData = overallData.map((cap) => ({
    id: cap.id,
    amount: cap.rawCap,
    period: cap.rawPeriod,
    start_date: cap.startDate,
    paced: cap.paced,
    status: cap.status === 'deleted' ? '-' : '1',
    partner_id: cap.partnerId,
    client_editable: cap.clientEditable,
  }));
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const { data } = await customFetch.post<CapReturnData>(
    `/client_caps.pl`,
    JSON.stringify(APIData),
    requestOptions,
  );
  if (!data) {
    throw new Error('Error updating caps');
  }
  if ('error' in data) {
    throw new Error(data.error);
  }
  return data;
};

// CapInputCell component for handling cap input with error states
interface CapInputCellProps {
  row: { original: FormattedCapsData[number] };
  showEditor: boolean;
  isDianomi: boolean | undefined;
  currency: string | undefined;
  onCapChange: (row: FormattedCapsData[number]) => void;
  onErrorChange: (hasError: boolean) => void;
}

const CapInputCell = ({
  row,
  showEditor,
  isDianomi,
  currency,
  onCapChange,
  onErrorChange,
}: CapInputCellProps) => {
  // Add error state management with hooks - must be called before any early returns
  const [error, setError] = React.useState<string | null>(null);
  const [touched, setTouched] = React.useState(false);

  const { period, rawCap, clientEditable } = row.original;
  const isDianomiOnly = clientEditable === 0 && !isDianomi;
  const disabled = !showEditor || isDianomiOnly;

  // Handle undefined values
  if (currency === undefined || isDianomi === undefined) {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);

    // Clear error when user starts typing
    if (error) {
      setError(null);
    }

    let errorMessage = null;

    // Validate input
    if (value < 0) {
      errorMessage = 'Value cannot be negative';
    } else if (Number.isNaN(value)) {
      errorMessage = 'Please enter a valid number';
    } else if (!Number.isInteger(value)) {
      errorMessage = 'Value must be a whole number';
    } else {
      errorMessage = null;
    }

    setError(errorMessage);
    // Report error state to parent
    onErrorChange(!!errorMessage);

    onCapChange({ ...row.original, rawCap: value });
  };

  const handleBlur = () => {
    setTouched(true);
    // Report error state to parent on blur
    onErrorChange(!!error);
  };

  if (!showEditor) {
    return (
      // extra padding to match the height of the input field when not disabled
      <Typography sx={{ paddingY: 2 }} variant="body2">
        {currency}
        {rawCap}
      </Typography>
    );
  }

  return (
    <Tooltip
      title={isDianomiOnly ? 'To edit this booking, please contact your account manager' : ''}
    >
      <div>
        <TextField
          type="number"
          id={`${period}Input`}
          name={`${period}Input`}
          label={period}
          value={rawCap}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          prefix={currency}
          data-testid={`${period}Input`}
          error={touched && !!error}
          helperText={touched && error}
          sx={{
            minWidth: '150px',
          }}
        />
      </div>
    </Tooltip>
  );
};

export const getTrippedStatus = (caps: FormattedCapsData | undefined) => {
  return (
    caps &&
    caps.length > 0 &&
    (caps.some((cap) => cap.tripped && !cap.partnerId) || caps.every((cap) => cap.tripped))
  );
};

function OverallCaps() {
  const clientSessionPlQuery = useClientSessionQuery();
  const isDianomi = clientSessionPlQuery?.data?.permissions.dianomi;
  const currency = clientSessionPlQuery?.data?.currency_pdf_name;
  const query = useOverallCaps(
    clientSessionPlQuery?.data?.id,
    clientSessionPlQuery?.data?.currency_pdf_name,
  );

  const queryClient = useQueryClient();
  const saveChangesMutation = useMutation({
    mutationFn: async (data: FormattedCapsData) => {
      return updateOverallCaps(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['overallCaps'],
      });
    },
  });

  const [dirtyData, setDirtyData] = React.useState<FormattedCapsData | null>(null);
  const [showEditor, setShowEditor] = React.useState(false);
  const [skipConfirmations, setSkipConfirmations] = React.useState(false);
  const [hasValidationErrors, setHasValidationErrors] = React.useState(false);
  const { setTimedNotification } = useNotifications();
  const displayData = dirtyData ?? query.data;
  const tripped = getTrippedStatus(query.data);
  const anyPubIdsInCaps = query?.data?.some((cap) => cap.partnerName !== null);
  const displayPublisherIds =
    !!anyPubIdsInCaps && !!clientSessionPlQuery?.data?.permissions?.displayPublisherIds;
  const displayPublisherNames =
    !!anyPubIdsInCaps && !!clientSessionPlQuery?.data?.permissions?.displayPublisherNames;
  const handleOverallEditClick = () => {
    if (!query.data) return;
    setShowEditor(!showEditor);
  };

  const handleChange = React.useCallback(
    (row: FormattedCapsData[number]) => {
      const newData = [...(displayData || [])];
      const index = newData.findIndex((cap) => cap.id === row.id);
      if (index !== -1) {
        newData[index] = row;
      }
      setDirtyData(newData);
    },
    [displayData],
  );

  const allowEditCap = clientSessionPlQuery?.data?.permissions?.allowBookingsManager === 'edit';
  const allowViewCaps = clientSessionPlQuery?.data?.permissions?.allowBookingsManager !== 'none';
  const accountManager = clientSessionPlQuery?.data?.client?.account_manager;

  // Check if all periods already have caps (excluding partner-specific caps)
  const existingPeriodsSet = new Set(
    query?.data
      ?.filter((cap) => !cap.partnerName && cap.status === 'active')
      .map((cap) => cap.rawPeriod) || [],
  );
  const allPeriodsHaveCaps = existingPeriodsSet.size >= 4; // Daily, Weekly, Monthly, Total

  const tableColumns = React.useMemo(() => {
    const columns: MRTColumnDef<FormattedCapsData[number]>[] = [
      {
        header: 'Start Date',
        accessorKey: 'startDate' as const,
        size: 0,
        Cell: ({ row }) => {
          return row.original.startDate || 'N/A';
        },
      },
      {
        header: 'Period',
        accessorKey: 'period' as const,
        size: 0,
        Cell: ({ row }) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {row.original.period}
            </Box>
          );
        },
      },
      {
        header: 'Booking',
        accessorKey: 'cap' as const,
        size: 150,

        Cell: ({ row }) => (
          <CapInputCell
            row={row}
            showEditor={showEditor}
            isDianomi={isDianomi}
            currency={currency}
            onCapChange={handleChange}
            onErrorChange={(hasError) => {
              setHasValidationErrors(hasError);
            }}
          />
        ),
      },
      {
        header: 'Paced',
        accessorKey: 'paced' as const,
        size: 0,
        Cell: ({ row }) => {
          const { paced, clientEditable } = row.original;
          const isDianomiOnly = clientEditable === 0 && !isDianomi;
          const disabled = !showEditor || isDianomiOnly;
          if (
            row.original.partnerName ||
            row.original.rawPeriod === 'O' ||
            row.original.rawPeriod === 'D'
          ) {
            return 'N/A';
          }
          if (!showEditor) {
            return paced ? 'Yes' : 'No';
          }
          return (
            <Tooltip
              title={
                isDianomiOnly ? 'To edit this booking, please contact your account manager' : ''
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Switch
                  label=""
                  checked={!!paced}
                  disabled={disabled}
                  onClick={() => handleChange({ ...row.original, paced: paced ? 0 : 1 })}
                />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        header: 'Remaining',
        accessorKey: 'remaining' as const,
        size: 0,
      },
    ];

    if (displayPublisherNames) {
      columns.push({ header: 'Publisher', accessorKey: 'partnerName' as const, size: 0 });
    }

    if (displayPublisherIds) {
      columns.push({
        header: 'Publisher ID',
        accessorKey: 'partnerId' as const,
        size: 0,
        Cell: ({ renderedCellValue }) => {
          return renderedCellValue === -1 || renderedCellValue === 0 ? null : renderedCellValue;
        },
      });
    }
    if (isDianomi) {
      columns.push({
        header: 'Client Editable',
        accessorKey: 'clientEditable' as const,
        size: 0,
        Cell: ({ row }) => {
          if (!showEditor) {
            return (
              <Typography sx={{ paddingY: 1 }} variant="body2">
                {row.original.clientEditable ? 'Yes' : 'No'}
              </Typography>
            );
          }
          return (
            <Switch
              label=""
              checked={!!row.original.clientEditable}
              disabled={!showEditor}
              onClick={() =>
                handleChange({
                  ...row.original,
                  clientEditable: row.original.clientEditable ? 0 : 1,
                })
              }
            />
          );
        },
      });
    }
    if (showEditor) {
      columns.push({
        header: 'Delete',
        accessorKey: 'status' as const,
        size: 0,
        Cell: ({ row }) => {
          const { clientEditable } = row.original;
          const isDianomiOnly = clientEditable === 0 && !isDianomi;
          const disabled = !showEditor || isDianomiOnly;
          return (
            <Button
              variant="contained"
              sx={{
                borderColor: 'error.main',
                color: 'error.main',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'white',
                },
              }}
              color="secondary"
              size="small"
              disabled={disabled}
              onClick={() => {
                if (skipConfirmations) {
                  // Skip confirmation if user chose to skip for this session
                  handleChange({ ...row.original, status: 'deleted' });
                } else {
                  // Show confirmation dialog
                  dialog.open({
                    title: 'Confirm Deletion',
                    body: (
                      <div>
                        <p>
                          Are you sure you want to delete this {row.original.period} booking? You
                          can revert this action by clicking &quot;Cancel&quot; before saving your
                          changes.
                        </p>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                          <StyledCheckbox
                            onChange={(e, checked) => {
                              setSkipConfirmations(checked);
                            }}
                            label="Skip confirmations for this session"
                          />
                        </Box>
                      </div>
                    ),
                    acceptText: 'Delete',
                    cancelText: 'Cancel',
                    onClose: () => {
                      setSkipConfirmations(false);
                      dialog.close();
                    },
                    onAccept: () => {
                      handleChange({ ...row.original, status: 'deleted' });
                      dialog.close();
                    },
                  });
                }
              }}
            >
              Delete
            </Button>
          );
        },
      });
    }

    return columns;
  }, [
    displayPublisherNames,
    displayPublisherIds,
    isDianomi,
    showEditor,
    currency,
    handleChange,
    skipConfirmations,
  ]);

  if (!allowViewCaps) {
    return null;
  }

  const filteredData = displayData?.filter((cap) => cap.status === 'active');
  const isDataDirty =
    dirtyData !== null && JSON.stringify(dirtyData) !== JSON.stringify(query.data || []);
  return (
    <>
      <Box sx={styles.overAllWrapper}>
        {!query.isLoading && (
          <Fade in>
            <div>
              <GridContainer>
                <GridItem xs={12}>
                  {tripped && !allowEditCap && (
                    <Card>
                      <CardBody sx={styles.overAllTrippedWarning}>
                        {`All ads in this account are not live due to the triggered cap below. If the cap is recurring, your active campaigns will be reactivated automatically in the new period. If it's a total cap, you need to expand it to continue running ads. To edit your caps please contact your account manager ${accountManager?.name} at ${accountManager?.email}`}
                      </CardBody>
                    </Card>
                  )}
                  {tripped && allowEditCap && (
                    <Card>
                      <CardBody sx={styles.overAllTrippedWarning}>
                        All ads in this account are not live due to the triggered cap below. If the
                        cap is recurring, your active campaigns will go back live automatically in
                        the new period. If it&apos;s a total cap, you need to expand the budget to
                        continue running ads
                      </CardBody>
                    </Card>
                  )}

                  <Collapse in={isDataDirty}>
                    <Card>
                      <CardBody
                        sx={(theme) => ({
                          padding: '10px',
                          fontSize: '0.8em',
                          background: theme.palette.warning.main,
                          borderRadius: '4px',
                        })}
                      >
                        You have unsaved changes. Click &apos;Save Changes&apos; to apply your
                        changes.
                      </CardBody>
                    </Card>
                  </Collapse>

                  <DataTable
                    state={{
                      isLoading: query.isFetching,
                    }}
                    muiTableBodyRowProps={({ row }) => ({
                      sx: {
                        ...(row.original.tripped ? { backgroundColor: 'rgb(255 208 205)' } : {}),
                      },
                    })}
                    data={filteredData || []}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    columns={tableColumns}
                    renderEmptyRowsFallback={() => (
                      <tr>
                        <td
                          colSpan={tableColumns.length}
                          style={{ textAlign: 'center', padding: '20px' }}
                        >
                          No account-wide bookings found
                        </td>
                      </tr>
                    )}
                  />
                </GridItem>
              </GridContainer>
            </div>
          </Fade>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
        }}
      >
        {allowEditCap && (
          <>
            {showEditor ? (
              <>
                <Button
                  variant="contained"
                  sx={(theme) => ({
                    ...styles.button,
                    color: theme.palette.secondary.main,
                  })}
                  onClick={() => {
                    if (hasValidationErrors) {
                      setTimedNotification(
                        'danger',
                        'Please fix all validation errors before saving changes.',
                        6000,
                      );
                      return;
                    }

                    saveChangesMutation.mutate(dirtyData ?? [], {
                      onSuccess: () => {
                        setDirtyData(null);
                        setShowEditor(false);
                        setHasValidationErrors(false);
                      },
                    });
                  }}
                  color={isDataDirty ? 'success' : 'primary'}
                  disabled={saveChangesMutation.isPending}
                >
                  Save Changes
                </Button>
                <Button
                  variant="contained"
                  sx={styles.button}
                  onClick={() => {
                    setDirtyData(null);
                    setShowEditor(false);
                    setSkipConfirmations(false);
                  }}
                  color="primary"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {allPeriodsHaveCaps ? (
                  <Tooltip title="All booking periods already have caps. Edit existing bookings instead.">
                    <div>
                      <Button
                        variant="contained"
                        sx={styles.button}
                        color="primary"
                        disabled
                        title="All booking periods already have caps. Edit existing bookings instead."
                      >
                        Add New Booking
                      </Button>
                    </div>
                  </Tooltip>
                ) : (
                  <Button
                    variant="contained"
                    sx={styles.button}
                    color="primary"
                    component={CustomNavLink}
                    to="create-booking"
                  >
                    Add New Booking
                  </Button>
                )}
                <Button
                  variant="contained"
                  sx={styles.button}
                  onClick={handleOverallEditClick}
                  color="primary"
                >
                  Edit Bookings
                </Button>
                <Button
                  variant="contained"
                  type="button"
                  color="primary"
                  sx={styles.button}
                  onClick={() => {
                    dialog.open({
                      body: (
                        <ForecastProvider>
                          <Forecaster />
                        </ForecastProvider>
                      ),
                      title: 'Forecaster',
                      width: 'xl',
                    });
                  }}
                >
                  Forecaster
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
}

export default OverallCaps;
